import { z } from 'zod';

// Versioned localStorage wrapper (§14.2). Every stored blob carries `v`; a corrupt or unknown-version value is
// reset to the default and the raw preserved under `<key>.bak` for recovery (T-CLI-012). Never throws — bad
// storage degrades to defaults, never a crash. The storage area is injected so tests use an in-memory fake.

export interface StorageArea {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class VersionedStore<T> {
  constructor(
    private readonly key: string,
    private readonly schema: z.ZodType<T>,
    private readonly fallback: () => T,
    private readonly area: StorageArea,
    /** Upgrade an older-version blob to the current shape before validation (§14.2 "zod + version migration"). */
    private readonly migrate: (raw: unknown) => unknown = (raw) => raw,
  ) {}

  /**
   * Load the value: an older-version blob is MIGRATED forward (data preserved, T-CLI-006); an absent, corrupt,
   * or still-invalid-after-migration (e.g. unknown FUTURE version) blob resets to the default, preserving the
   * raw as `.bak` (T-CLI-012).
   */
  load(): T {
    const raw = this.area.getItem(this.key);
    if (raw === null) return this.fallback();
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return this.reset(raw);
    }
    const result = this.schema.safeParse(this.migrate(parsed));
    return result.success ? result.data : this.reset(raw);
  }

  save(value: T): void {
    this.area.setItem(this.key, JSON.stringify(value));
  }

  clear(): void {
    this.area.removeItem(this.key);
  }

  private reset(corruptRaw: string): T {
    // load() must NEVER throw (a quota-exhausted / private-mode area can throw from setItem/removeItem) — a
    // failed .bak or clear still degrades to defaults rather than crashing the caller (review C3).
    try {
      this.area.setItem(`${this.key}.bak`, corruptRaw); // keep the bad data for recovery/debugging (T-CLI-012)
      this.area.removeItem(this.key);
    } catch {
      /* storage unavailable — fall through to defaults */
    }
    return this.fallback();
  }
}

// ── Schemas (§14.2) ──────────────────────────────────────────────────────────
export const guestSchema = z.object({
  v: z.literal(1),
  guestId: z.string(),
  name: z.string(),
  kit: z.string().optional(),
  stats: z.record(z.number()).optional(),
});
export type GuestState = z.infer<typeof guestSchema>;

export const settingsSchema = z.object({
  v: z.literal(1),
  bindings: z.record(z.string()).optional(),
  joinMuted: z.boolean(),
  ptt: z.boolean(),
  volumes: z.record(z.number()).optional(),
  colorblindKits: z.boolean(),
});
export type SettingsState = z.infer<typeof settingsSchema>;

export const sessionSchema = z.object({
  v: z.literal(1),
  authToken: z.string().optional(),
  resumeRoom: z.object({ code: z.string(), gameSessionToken: z.string() }).optional(),
});
export type SessionState = z.infer<typeof sessionSchema>;

export const STORAGE_KEYS = { guest: 'fafi.guest', settings: 'fafi.settings', session: 'fafi.session', tutorial: 'fafi.tutorial' } as const;

// D138 (§8.1) — tutorial progress. One entry per COMPLETED lesson; a lesson absent from the map is not
// started. `at` is epoch ms (the completion moment), `attempts` how many goes it took, `bonus` whether
// the ★ was earned. Corruption/`.bak` reset comes free from VersionedStore.
export const tutorialEntrySchema = z.object({ at: z.number().int().nonnegative(), attempts: z.number().int().positive(), bonus: z.boolean() });
export const tutorialSchema = z.object({ v: z.literal(1), completed: z.record(z.string(), tutorialEntrySchema) });
export type TutorialEntry = z.infer<typeof tutorialEntrySchema>;
export type TutorialState = z.infer<typeof tutorialSchema>;
export const defaultTutorial = (): TutorialState => ({ v: 1, completed: {} });

/**
 * D138 — merge two progress blobs, UNION of completions: earliest `at` wins, fewest `attempts` wins, and
 * the ★ is sticky (OR). Commutative and idempotent by construction, which is what makes local↔server
 * reconciliation safe in either direction and in any order.
 *
 * The result is built on a NULL-PROTOTYPE map: a lesson id is attacker-controlled data on the server
 * side, and `__proto__` as a key must be an ordinary entry, never a prototype write.
 */
export function mergeTutorial(a: TutorialState, b: TutorialState): TutorialState {
  const completed: Record<string, TutorialEntry> = Object.create(null) as Record<string, TutorialEntry>;
  for (const src of [a.completed, b.completed]) {
    for (const id of Object.keys(src)) {
      const e = src[id];
      if (e === undefined) continue;
      const cur = completed[id];
      completed[id] = cur === undefined ? { ...e } : { at: Math.min(cur.at, e.at), attempts: Math.min(cur.attempts, e.attempts), bonus: cur.bonus || e.bonus };
    }
  }
  return { v: 1, completed };
}

export const defaultSettings = (): SettingsState => ({ v: 1, joinMuted: true, ptt: false, colorblindKits: false });
export const defaultSession = (): SessionState => ({ v: 1 });

/**
 * Migrate a stored guest profile forward to the current schema (T-CLI-006). v0 (a pre-versioning blob with no
 * `v`, or an explicit `v:0`) is stamped `v:1` with its fields carried over — data preserved. A blob already at
 * v1 passes through; an unknown FUTURE version is returned unchanged so the schema rejects it → reset (T-CLI-012).
 */
export function migrateGuest(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const o = raw as Record<string, unknown>;
  if (o['v'] === undefined || o['v'] === 0) {
    const up: Record<string, unknown> = { v: 1, guestId: o['guestId'] ?? '', name: o['name'] ?? '' };
    if (o['kit'] !== undefined) up['kit'] = o['kit'];
    if (o['stats'] !== undefined) up['stats'] = o['stats'];
    return up;
  }
  return o;
}

export function makeStores(area: StorageArea) {
  return {
    settings: new VersionedStore(STORAGE_KEYS.settings, settingsSchema, defaultSettings, area),
    session: new VersionedStore(STORAGE_KEYS.session, sessionSchema, defaultSession, area),
    guest: (fallback: () => GuestState) => new VersionedStore(STORAGE_KEYS.guest, guestSchema, fallback, area, migrateGuest),
    tutorial: new VersionedStore(STORAGE_KEYS.tutorial, tutorialSchema, defaultTutorial, area), // D138
  };
}
